import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import type {
  TransformedProduct,
  TransformedVariation,
  EnrichedAttributeTransformed,
} from "../../types/productApiTypes";

interface ProductPanelsProps {
  product: TransformedProduct;
  isLoading: boolean;
  selectedVariation?: TransformedVariation | null;
}

const ProductPanels: React.FC<ProductPanelsProps> = ({
  product,
  isLoading,
  selectedVariation = null,
}) => {
  const { t } = useTranslation("common");
  const description = product.description;
  const enrichedAttributes = product.enrichedAttributes || [];
  const displayDescription = selectedVariation?.description || description;

  const hasDescription = displayDescription && displayDescription.trim() !== "";
  const hasAttributes = enrichedAttributes.length > 0;

  const [activePanel, setActivePanel] = useState<string | null>(null);

  const handlePanelClick = (panelId: string): void => {
    setActivePanel(activePanel === panelId ? null : panelId);
  };

  if (isLoading) {
    return <ProductPanelsSkeleton />;
  }

  return (
    <div className="product-sections">
      {hasDescription && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "description" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("description")}
            aria-expanded={activePanel === "description"}
            aria-controls="description-panel"
            id="description-header"
          >
            {t("productPanels.description")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="description-panel"
            className={`panel-content ${
              activePanel === "description" ? "active" : ""
            }`}
            role="region"
            aria-labelledby="description-header"
          >
            <div className="panel-content__inner">
              <div dangerouslySetInnerHTML={{ __html: displayDescription }} />
            </div>
          </div>
        </div>
      )}

      {hasAttributes && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "properties" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("properties")}
            aria-expanded={activePanel === "properties"}
            aria-controls="properties-panel"
            id="properties-header"
          >
            {t("productPanels.additionalInfo")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="properties-panel"
            className={`panel-content ${
              activePanel === "properties" ? "active" : ""
            }`}
            role="region"
            aria-labelledby="properties-header"
          >
            <div className="panel-content__inner">
              <table className="product-attributes">
                <tbody>
                  {enrichedAttributes.map(
                    (attribute: EnrichedAttributeTransformed) => {
                      const termNames = attribute.terms.map(
                        (term) => term.name
                      );

                      if (termNames.length === 0) {
                        return null;
                      }

                      return (
                        <tr key={attribute.id}>
                          <th>{attribute.name}</th>
                          <td>{termNames.join(", ")}</td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductPanelsSkeleton: React.FC = () => {
  const sectionCount = 2;
  return (
    <div className="product-sections-loading">
      {[...Array(sectionCount)].map((_, index) => (
        <div className="product-section-skeleton" key={index}>
          <Skeleton height={45} className="section-header-skeleton" />
        </div>
      ))}
    </div>
  );
};

type ProductPanelsComponent = React.FC<ProductPanelsProps> & {
  Skeleton: React.FC<{}>;
};
(ProductPanels as ProductPanelsComponent).Skeleton = ProductPanelsSkeleton;

export default ProductPanels as ProductPanelsComponent;
